import { useState, useContext } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Stack, Step, StepLabel, Stepper,
  TextField, Typography,
} from '@mui/material';
import { CloseOutlined, WarningAmberOutlined } from '@mui/icons-material';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import type { ISurveyExtrapolationPreview } from '../../../shared/services/survey/survey.model';

function fmtNum(n: number | null | undefined, digits = 4): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

interface Props {
  open: boolean;
  surveyId: string;
  committedResponseCount: number;
  onClose: () => void;
  onCreated: () => void;
}

export const SurveyExtrapolationDialog = ({ open, surveyId, committedResponseCount, onClose, onCreated }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [step, setStep] = useState(0);
  const [targetCount, setTargetCount] = useState('');
  const [preview, setPreview] = useState<ISurveyExtrapolationPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setStep(0);
    setTargetCount('');
    setPreview(null);
    setError(null);
    onClose();
  };

  const handlePreview = async () => {
    const n = parseInt(targetCount, 10);
    if (!Number.isFinite(n) || n < 1) {
      setError('Please enter a valid target response count.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await surveyAdminService.previewExtrapolation(surveyId, n);
      setPreview(data);
      setStep(1);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not load preview.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    const n = parseInt(targetCount, 10);
    setSubmitting(true);
    try {
      await surveyAdminService.createExtrapolation(surveyId, n);
      userNotificationsContext.openSnackbar({ open: true, message: 'Extrapolation created', backgroundColor: 'primary.dark' });
      onCreated();
      handleClose();
    } catch (err: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message ?? 'Failed to create extrapolation',
        backgroundColor: 'text.primary',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const pct = preview ? `${((preview.scaleFactor - 1) * 100).toFixed(1)}%` : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: 5 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6' component='span' fontWeight={600}>Extrapolate survey results</Typography>
          <IconButton onClick={handleClose} sx={{ mr: -0.75 }}><CloseOutlined /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step><StepLabel>Target count</StepLabel></Step>
          <Step><StepLabel>Confirm</StepLabel></Step>
        </Stepper>

        {step === 0 && (
          <Stack spacing={2}>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'inline-flex', gap: 1, alignItems: 'center' }}>
              <Typography variant='caption' color='text.secondary'>Currently committed responses:</Typography>
              <Typography variant='body2' fontWeight={700}>{committedResponseCount}</Typography>
            </Box>
            <Typography variant='body2' color='text.secondary'>
              Enter the total number of responses you want to extrapolate to. The system will scale the
              already-committed emissions by the difference (e.g. if you have {committedResponseCount} committed responses and enter{' '}
              {committedResponseCount * 2}, it will add emissions equal to 100% of the current committed amount).
            </Typography>
            <TextField
              label='Target response count'
              type='number'
              value={targetCount}
              onChange={e => { setTargetCount(e.target.value); setError(null); }}
              inputProps={{ min: committedResponseCount + 1, step: 1 }}
              fullWidth
              required
            />
            {error && <Typography color='error.main' variant='body2'>{error}</Typography>}
          </Stack>
        )}

        {step === 1 && preview && (
          <Stack spacing={2}>
            <Stack direction='row' spacing={4}>
              <Box>
                <Typography variant='caption' color='text.secondary'>Committed responses</Typography>
                <Typography variant='h6' fontWeight={700}>{preview.actualResponseCount}</Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>Target responses</Typography>
                <Typography variant='h6' fontWeight={700}>{preview.targetResponseCount}</Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>Scale factor</Typography>
                <Typography variant='h6' fontWeight={700} color='primary.main'>
                  ×{fmtNum(preview.scaleFactor, 3)} <Typography component='span' variant='body2' color='text.secondary'>(+{pct})</Typography>
                </Typography>
              </Box>
            </Stack>
            {preview.scaleFactor > 1.2 && (
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                <Stack direction='row' spacing={1} alignItems='flex-start'>
                  <WarningAmberOutlined sx={{ color: 'warning.main', mt: 0.1, flexShrink: 0 }} fontSize='small' />
                  <Box>
                    <Typography variant='body2' fontWeight={600} sx={{ mb: 0.5 }}>Please note</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Extrapolating more than 20% beyond your dataset's range introduces uncertainty and generally produces lower quality data.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
            <Divider />
            <Typography variant='subtitle2'>Emission records that will be created</Typography>
            <Stack spacing={1}>
              {preview.lines.map((line, i) => (
                <Box key={i} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant='body2' fontWeight={600}>{line.ruleName || `Rule ${i + 1}`}</Typography>
                  <Stack direction='row' spacing={3} sx={{ mt: 0.5 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Original: <strong>{fmtNum(line.originalUnitsConsumed)}{line.activityUnitName ? ` ${line.activityUnitName}` : ''}</strong>
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Adding: <strong>+{fmtNum(line.extrapolatedUnitsConsumed)}{line.activityUnitName ? ` ${line.activityUnitName}` : ''}</strong>
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
            <Typography variant='caption' color='text.secondary'>
              While an extrapolation is active, you cannot commit more survey responses to emissions. Reverse the
              extrapolation first if you receive additional responses.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={step === 1 ? () => setStep(0) : handleClose} disabled={loading || submitting}>
          {step === 1 ? '← Back' : 'Cancel'}
        </Button>
        {step === 0 ? (
          <Button
            variant='contained'
            onClick={handlePreview}
            disabled={!targetCount || loading}
          >
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Preview →
          </Button>
        ) : (
          <Button variant='contained' onClick={handleConfirm} disabled={submitting}>
            {submitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Confirm extrapolation
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
